import { DocumentFile, useDocuments } from "@/contexts/DocumentContext";
import { DocumentViewer } from "./DocumentViewer";
import { FileText, Image, File, Trash2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";

interface DocumentListProps {
  documents?: DocumentFile[];
  entityId?: string;
  showCategory?: boolean;
}

const FILE_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  "application/pdf": FileText,
  "image/jpeg": Image,
  "image/png": Image,
};

function formatSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function DocumentList({ documents: docsProp, entityId, showCategory = true }: DocumentListProps) {
  const { documents: allDocs, deleteDocument, getDocumentsByEntity } = useDocuments();
  const docs = docsProp || (entityId ? getDocumentsByEntity(entityId) : allDocs);

  if (!docs.length) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <FileText className="h-10 w-10 mx-auto mb-2 opacity-30" />
        <p className="text-sm">No documents uploaded yet</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {docs.map((doc) => {
        const Icon = FILE_ICONS[doc.type] || File;
        return (
          <div key={doc.id} className="flex items-center gap-3 px-4 py-3 rounded-lg border border-border bg-card hover:shadow-soft transition-shadow">
            <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Icon className="h-4 w-4 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-foreground truncate">{doc.name}</p>
              <div className="flex items-center gap-2 mt-0.5">
                <span className="text-[10px] text-muted-foreground">{formatSize(doc.size)}</span>
                <span className="text-[10px] text-muted-foreground">•</span>
                <span className="text-[10px] text-muted-foreground">
                  {new Date(doc.uploadedAt).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}
                </span>
                {showCategory && (
                  <Badge variant="secondary" className="text-[9px] px-1.5 py-0">
                    {doc.category}
                  </Badge>
                )}
              </div>
            </div>
            <div className="flex items-center gap-1.5">
              <DocumentViewer document={doc} />
              <button
                onClick={() => deleteDocument(doc.id)}
                className="text-muted-foreground hover:text-destructive transition-colors p-1"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
